import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import BaseBlock from '@components/Patient/Blocks/BaseBlock';
import PatientPicker, { type PatientPickerRef } from '@components/Patient/PatientPicker';
import TemplatePicker from '@components/Pickers/TemplatePicker';
import { CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { useAuditEnabled } from '@features/jotaudit';
import { useGetTemplatesQuery } from '@features/templates';
import { useSubjectLanguage, useSubjectLanguageVariants } from '@features/user';
import { Clear, Description, Event, Person, Receipt } from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type Patient } from '@ts/types';
import { formatPatientName } from '@utils/patientUtils';
import { capitalize } from '@utils/utils';
import { format } from 'date-fns';

import type { EncounterFormData } from '../../types';

import EncounterScheduleDuration from './EncounterScheduleDuration';

// Define the possible states for our state machine
export type SelectorState =
  | 'IDLE'
  | 'SELECTING_PATIENT'
  | 'SELECTING_TEMPLATE'
  | 'SELECTING_SCHEDULE'
  | 'SELECTING_CPT';

// Define the ref interface to expose methods to parent components
export interface EncounterSelectorButtonsRef {
  setCurrentState: (state: SelectorState) => void;
}

interface EncounterSelectorButtonsProps {
  encounterId?: string; // Optional during migration
  isViewOnly: boolean;
  hasAudioDocuments: boolean;
  vertical?: boolean;
}

const EncounterSelectorButtons = forwardRef<
  EncounterSelectorButtonsRef,
  EncounterSelectorButtonsProps
>(({ encounterId, isViewOnly, hasAudioDocuments, vertical = false }, ref) => {
  const subjectLanguage = useSubjectLanguage();
  const { singularCap: subjectLanguageCap = 'Patient' } = useSubjectLanguageVariants() || {};
  const { data: templatesData } = useGetTemplatesQuery();
  const isAuditEnabled = useAuditEnabled();

  // Transform V2 templates to UserTemplateItem format for compatibility
  const templatesList = useMemo(() => {
    if (!templatesData?.templates) {
      return [];
    }
    return templatesData.templates.map(item => ({
      template_id: item.template.id,
      display_name: item.revision.title,
      type: item.template.owner_scope === 'user' ? 'user' : ('public' as 'user' | 'public'),
    }));
  }, [templatesData]);

  // Form context (replaces RTK Query hooks)
  const methods = useFormContext<EncounterFormData>();
  const { watch, setValue } = methods;

  // Watch multiple form values with a single subscription for better performance
  const formValues = useWatch({
    name: [
      'patient_id',
      'patient',
      'note_title',
      'template_id',
      'encounter_type',
      'appointment',
      'cpt_codes',
    ],
  });

  // Destructure for easier access
  const [patient_id, patient, note_title, template_id, encounter_type, appointment, cpt_codes] =
    formValues;

  // State machine state
  const [currentState, setCurrentState] = React.useState<SelectorState>('IDLE');
  const [noteTitle, setNoteTitle] = React.useState(note_title || '');

  // Expose setCurrentState method to parent components
  useImperativeHandle(ref, () => ({
    setCurrentState,
  }));

  // Check if each selector is filled (use form values instead of encounter)
  const isPatientFilled = Boolean(patient_id || note_title);
  const isTemplateFilled = Boolean(template_id || encounter_type);
  const isScheduleFilled = Boolean(appointment);
  const isCptFilled = Boolean(
    cpt_codes && cpt_codes.length > 0 && cpt_codes.some((c: string) => c && c.trim() !== ''),
  );

  // Update local state when form value changes
  useEffect(() => {
    setNoteTitle(note_title || '');
  }, [note_title]);

  // Get text for schedule button
  const getScheduleButtonText = () => {
    try {
      if (appointment) {
        const scheduledDate = format(new Date(appointment.start_time), 'MMM d, h:mm a');

        // Add duration information if available
        if (appointment) {
          return `${scheduledDate} · ${formatDurationText(appointment.duration)}`;
        }

        return scheduledDate;
      } else {
        return 'Select date and duration';
      }
    } catch (error) {
      console.error('Error formatting schedule button text:', error);
      return 'Select date and duration';
    }
  };

  // Format duration as text from minutes
  const formatDurationText = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0
      ? `${hours}h ${remainingMinutes > 0 ? `${remainingMinutes}m` : ''}`
      : `${remainingMinutes}m`;
  };

  // Get label text for each selector button
  const getSelectorLabel = (type: string) => {
    switch (type) {
      case 'patient':
        return patient
          ? patient.name
          : note_title
            ? note_title
            : `Select ${capitalize(subjectLanguage?.toLowerCase() || '')}`;
      case 'template':
        const template = templatesList?.find(t => t.template_id === template_id);
        return template?.display_name || 'Select template';
      case 'schedule':
        return getScheduleButtonText();
      case 'cpt':
        if (
          !cpt_codes ||
          cpt_codes.length === 0 ||
          !cpt_codes.some((c: string) => c && c.trim() !== '')
        ) {
          return 'Add billing codes';
        }
        return cpt_codes.filter((c: string) => c && c.trim() !== '').join(', ');
      default:
        return '';
    }
  };

  // State machine transition handlers
  const transitionTo = (newState: SelectorState) => {
    setCurrentState(newState);
  };

  // Added a field parameter to know which field was just filled
  const autoProgressToNext = useCallback(
    (justFilledField?: 'patient' | 'template' | 'schedule' | 'cpt') => {
      // First close the current selector
      setCurrentState('IDLE');

      // After a small delay, move to the next unfilled state if there is one
      setTimeout(() => {
        // Get current fill status but override the field that was just filled
        let nextUnfilled: SelectorState = 'IDLE';

        // Assuming the field that was just updated is now filled
        const patientJustFilled = justFilledField === 'patient' || isPatientFilled;
        const templateJustFilled = justFilledField === 'template' || isTemplateFilled;
        const scheduleJustFilled = justFilledField === 'schedule' || isScheduleFilled;
        // We don't auto-progress TO CPT codes as they are optional, but we want to include them in the logic if needed
        // For now, let's keep the core flow focused on required fields

        // Find the next unfilled selector, taking into account what was just filled
        if (!patientJustFilled) nextUnfilled = 'SELECTING_PATIENT';
        else if (!templateJustFilled) nextUnfilled = 'SELECTING_TEMPLATE';
        else if (!scheduleJustFilled) nextUnfilled = 'SELECTING_SCHEDULE';

        if (nextUnfilled !== 'IDLE') {
          setCurrentState(nextUnfilled);
        }
      }, 100);
    },
    [setCurrentState, isPatientFilled, isTemplateFilled, isScheduleFilled, isViewOnly],
  );

  useEffect(() => {
    autoProgressToNext();
  }, [encounterId, autoProgressToNext]);

  // Create a ref for the PatientPicker
  const patientPickerRef = useRef<PatientPickerRef>(null);

  // Focus the PatientPicker input when selecting patient opens
  useEffect(() => {
    if (currentState === 'SELECTING_PATIENT') {
      setTimeout(() => {
        if (patientPickerRef.current) {
          patientPickerRef.current.focusInput();
        }
      }, 150); // A slightly longer delay to ensure the selector is visible
    }
  }, [currentState]);

  // Handler functions for the form elements
  const handlePatientSelect = (selectedPatient: Patient | undefined) => {
    if (isViewOnly) return;

    if (selectedPatient) {
      // Transform Patient to PatientInfo structure (form expects PatientInfo with 'name' field)
      // Backend returns PatientInfo with computed 'name', but PatientPicker gives us Patient with first_name/last_name
      const patientInfo = {
        patient_id: selectedPatient.patient_id,
        name: formatPatientName(
          selectedPatient.first_name,
          selectedPatient.last_name,
          selectedPatient.preferred_name,
        ),
        dob: selectedPatient.dob,
        identifier: selectedPatient.identifier,
      };

      setValue('patient_id', selectedPatient.patient_id, { shouldDirty: true });
      setValue('patient', patientInfo, { shouldDirty: true }); // Store PatientInfo, not Patient
      setValue('note_title', null, { shouldDirty: true });

      setNoteTitle('');
      autoProgressToNext('patient');
    }
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNoteTitle(event.target.value);
  };

  const handleTitleSubmit = () => {
    if (isViewOnly || !noteTitle.trim()) return;

    setValue('note_title', noteTitle.trim(), { shouldDirty: true });
    setValue('patient_id', null, { shouldDirty: true });
    setValue('patient', null, { shouldDirty: true });

    autoProgressToNext('patient');
  };

  const handleTitleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleTitleSubmit();
    }
  };

  const handleTemplateSelect = (template: any) => {
    if (isViewOnly) return;

    console.log('[EncounterSelector] Template selected:', template?.template_id);
    setValue('template_id', template?.template_id, { shouldDirty: true });
    setValue('encounter_type', template?.display_name, { shouldDirty: true });
    console.log('[EncounterSelector] Template setValue calls completed');

    autoProgressToNext('template');
  };

  const handleScheduleSelect = useCallback(
    (date: Date | undefined, duration: number | undefined) => {
      if (isViewOnly || !date || !duration) return;

      console.log('[EncounterSelector] Schedule selected:', { date, duration });
      setValue(
        'appointment',
        {
          start_time: date.toISOString(),
          duration: duration,
        },
        { shouldDirty: true },
      );
      console.log('[EncounterSelector] Schedule setValue completed');

      autoProgressToNext('schedule');
    },
    [isViewOnly, setValue, autoProgressToNext],
  );

  // Clear handlers
  const handleClearPatient = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isViewOnly) return;

    setValue('patient_id', null, { shouldDirty: true });
    setValue('patient', null, { shouldDirty: true });
    setValue('note_title', null, { shouldDirty: true });
    setNoteTitle('');
  };

  const handleClearTemplate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isViewOnly) return;

    setValue('template_id', null, { shouldDirty: true });
    setValue('encounter_type', null, { shouldDirty: true });
  };

  const handleClearSchedule = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isViewOnly) return;

    setValue('appointment', null, { shouldDirty: true });
    setValue('start_time', null, { shouldDirty: true });
    setValue('end_time', null, { shouldDirty: true });
  };

  const handleCptChange = useCallback(
    (codes: string[]) => {
      if (isViewOnly) return;
      setValue('cpt_codes', codes, { shouldDirty: true });
    },
    [isViewOnly, setValue],
  );

  const handleClearCpt = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isViewOnly) return;

    setValue('cpt_codes', [], { shouldDirty: true });
  };

  // Patient selector component
  const renderPatientSelector = () => {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ mb: 1, mt: 1 }}>
          <PatientPicker
            ref={patientPickerRef}
            onPatientSelect={handlePatientSelect}
            initialPatient={patient ?? undefined}
            autoFocus={currentState === 'SELECTING_PATIENT'}
          />
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">
            OR
          </Typography>
        </Divider>

        <Box sx={{ width: '100%' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Use a custom title (optional)
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              placeholder="Note Title"
              size="small"
              fullWidth
              value={noteTitle}
              onChange={handleTitleChange}
              onKeyDown={handleTitleKeyDown}
              sx={{ mr: 1 }}
              autoFocus={currentState === 'SELECTING_PATIENT' && !patient}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleTitleSubmit}
              disabled={!noteTitle.trim()}
              sx={{
                minWidth: '80px',
                height: '40px',
                boxShadow: 'none',
                color: 'white',
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  // Render a selector button with its content
  const renderSelector = (
    type: 'patient' | 'template' | 'schedule' | 'cpt',
    title: string,
    icon: React.ReactNode,
    isFilled: boolean,
    stateValue: SelectorState,
    clearHandler: (e: React.MouseEvent) => void,
    tooltipTitle: string,
  ) => {
    const isOpen = currentState === stateValue;

    return (
      <Box sx={{ width: '100%', mb: isOpen ? 1 : 0 }}>
        {/* Button */}
        <Paper
          elevation={0}
          onClick={isViewOnly ? undefined : () => transitionTo(isOpen ? 'IDLE' : stateValue)}
          sx={{
            py: 1,
            px: 1.5,
            width: '100%',
            cursor: isViewOnly ? 'default' : 'pointer',
            borderRadius: 1,
            border: '1px solid',
            borderColor: isFilled || isOpen ? 'primary.main' : 'divider',
            backgroundColor: isOpen ? 'action.selected' : 'background.paper',
            transition: 'all 0.2s',
            '&:hover': isViewOnly
              ? {}
              : {
                  borderColor: 'primary.main',
                  backgroundColor: isOpen ? 'action.selected' : 'action.hover',
                },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 'bold', fontSize: '0.7rem', lineHeight: 1.2 }}
            >
              {title}
            </Typography>
            {isFilled && !isViewOnly && (
              <IconButton size="small" onClick={clearHandler} sx={{ p: 0.5 }}>
                <Clear fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            <Typography variant="body2" color={isFilled ? 'text.primary' : 'text.secondary'} noWrap>
              {getSelectorLabel(type)}
            </Typography>
          </Box>
        </Paper>

        {/* Selector Content */}
        <Collapse in={isOpen} timeout="auto">
          <Box sx={{ mt: 1 }}>
            <BaseBlock sx={{ p: 1.5 }}>
              {type === 'patient' && renderPatientSelector()}

              {type === 'template' && (
                <Box sx={{ my: 1 }}>
                  <TemplatePicker
                    onTemplateSelect={handleTemplateSelect}
                    key={`template-picker-${encounterId}-${template_id || 'empty'}`}
                    initialTemplateId={template_id ?? undefined}
                  />
                </Box>
              )}

              {type === 'schedule' && (
                <EncounterScheduleDuration
                  encounterId={encounterId}
                  onScheduleSelect={handleScheduleSelect}
                  onClose={() => setCurrentState('IDLE')}
                />
              )}

              {type === 'cpt' && isAuditEnabled && (
                <Box sx={{ my: 1 }}>
                  <CPTCodePicker
                    value={cpt_codes}
                    onChange={handleCptChange}
                    disabled={isViewOnly}
                    showHeader={true}
                  />
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => autoProgressToNext('cpt')}
                    >
                      Done
                    </Button>
                  </Box>
                </Box>
              )}
            </BaseBlock>
          </Box>
        </Collapse>
      </Box>
    );
  };

  // Render the component based on vertical prop
  if (vertical) {
    return (
      <Stack spacing={1.5} width="100%">
        {renderSelector(
          'patient',
          subjectLanguageCap,
          <Person color={isPatientFilled ? 'primary' : 'disabled'} fontSize="small" />,
          isPatientFilled,
          'SELECTING_PATIENT',
          handleClearPatient,
          `Link ${subjectLanguage}`,
        )}

        {renderSelector(
          'template',
          'Session Type',
          <Description color={isTemplateFilled ? 'primary' : 'disabled'} fontSize="small" />,
          isTemplateFilled,
          'SELECTING_TEMPLATE',
          handleClearTemplate,
          'Select template',
        )}

        {renderSelector(
          'schedule',
          'Date & Time',
          <Event color={isScheduleFilled ? 'primary' : 'disabled'} fontSize="small" />,
          isScheduleFilled,
          'SELECTING_SCHEDULE',
          handleClearSchedule,
          'Set schedule and duration',
        )}

        {isAuditEnabled &&
          renderSelector(
            'cpt',
            'Billing Codes (Optional)',
            <Receipt color={isCptFilled ? 'primary' : 'disabled'} fontSize="small" />,
            isCptFilled,
            'SELECTING_CPT',
            handleClearCpt,
            'Enter CPT codes (optional)',
          )}
      </Stack>
    );
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={isAuditEnabled ? 3 : 4}>
        {renderSelector(
          'patient',
          subjectLanguageCap,
          <Person color={isPatientFilled ? 'primary' : 'disabled'} fontSize="small" />,
          isPatientFilled,
          'SELECTING_PATIENT',
          handleClearPatient,
          `Link ${subjectLanguage}`,
        )}
      </Grid>

      <Grid item xs={12} md={isAuditEnabled ? 3 : 4}>
        {renderSelector(
          'template',
          'Session Type',
          <Description color={isTemplateFilled ? 'primary' : 'disabled'} fontSize="small" />,
          isTemplateFilled,
          'SELECTING_TEMPLATE',
          handleClearTemplate,
          'Select template',
        )}
      </Grid>

      <Grid item xs={12} md={isAuditEnabled ? 3 : 4}>
        {renderSelector(
          'schedule',
          'Date & Time',
          <Event color={isScheduleFilled ? 'primary' : 'disabled'} fontSize="small" />,
          isScheduleFilled,
          'SELECTING_SCHEDULE',
          handleClearSchedule,
          'Set schedule and duration',
        )}
      </Grid>

      {isAuditEnabled && (
        <Grid item xs={12} md={3}>
          {renderSelector(
            'cpt',
            'Billing Codes (Optional)',
            <Receipt color={isCptFilled ? 'primary' : 'disabled'} fontSize="small" />,
            isCptFilled,
            'SELECTING_CPT',
            handleClearCpt,
            'Enter CPT codes (optional)',
          )}
        </Grid>
      )}
    </Grid>
  );
});

// Add display name for React DevTools
EncounterSelectorButtons.displayName = 'EncounterSelectorButtons';

export default EncounterSelectorButtons;
